import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRightLeft,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Copy,
  Download,
  Settings2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatDate, formatDateTime } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { TransmitterEditModal } from '@/features/operations-transmitters/components/TransmitterEditModal';
import { getAccountGateways } from '../api';
import ReassignDialog from './ReassignDialog';
import type { TransmitterDetail, AccountDashboardData } from '../types';

// =============================================================================
// Column Definition
// =============================================================================

interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T, helpers: GatewayRowHelpers) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

interface GatewayRowHelpers {
  onEdit: (gateway: TransmitterDetail) => void;
  onReassign: (gateway: TransmitterDetail) => void;
}

const STATUS_OPTIONS = [
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
];

const COLUMNS: ColumnDef<TransmitterDetail>[] = [
  {
    key: 'devId',
    label: 'Dev ID',
    sortKey: 'devId',
    filterType: 'text',
    getValue: (t) => t.devId,
    render: (t) => <span className="font-medium">{t.devId}</span>,
  },
  {
    key: 'name',
    label: 'Name',
    sortKey: 'name',
    filterType: 'text',
    getValue: (t) => t.name || '',
    render: (t) => (
      <span className="max-w-[200px] truncate block">{t.name || '—'}</span>
    ),
  },
  {
    key: 'siteName',
    label: 'Site',
    filterType: 'text',
    getValue: (t) => t.siteName || '',
  },
  {
    key: 'locationName',
    label: 'Location',
    filterType: 'text',
    defaultVisible: false,
    getValue: (t) => t.locationName || '',
  },
  {
    key: 'status',
    label: 'Status',
    filterType: 'select',
    filterOptions: STATUS_OPTIONS,
    getValue: (t) => String(t.activeFlag),
    render: (t) => (
      <Badge variant={t.activeFlag ? 'success' : 'secondary'}>
        {t.activeFlag ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
  {
    key: 'lastpacketAt',
    label: 'Last Packet',
    sortKey: 'lastpacketAt',
    filterType: 'date-range',
    getValue: (t) => t.lastpacketAt || '',
    render: (t) => (t.lastpacketAt ? formatDate(t.lastpacketAt) : '—'),
  },
  {
    key: 'awsIotLastUplinkAt',
    label: 'Last Uplink',
    sortKey: 'awsIotLastUplinkAt',
    filterType: 'date-range',
    getValue: (t) => t.awsIotLastUplinkAt || '',
    render: (t) => (t.awsIotLastUplinkAt ? formatDateTime(t.awsIotLastUplinkAt) : '—'),
  },
  {
    key: 'rssi',
    label: 'RSSI',
    sortKey: 'rssi',
    filterType: 'number-range',
    defaultVisible: false,
    getValue: (t) => t.rssi ?? null,
    render: (t) => {
      if (t.rssi == null) return '—';
      const color =
        t.rssi > -70
          ? 'text-green-600'
          : t.rssi > -90
            ? 'text-yellow-600'
            : 'text-red-600';
      return <span className={color}>{t.rssi}</span>;
    },
    align: 'right',
  },
  {
    key: 'currentFirmwareName',
    label: 'Firmware',
    filterType: 'text',
    defaultVisible: false,
    getValue: (t) => t.currentFirmwareName || '',
  },
  {
    key: 'simprovider',
    label: 'SIM Provider',
    filterType: 'text',
    defaultVisible: false,
    getValue: (t) => t.simprovider || '',
  },
  {
    key: 'iccid',
    label: 'ICCID',
    filterType: 'text',
    defaultVisible: false,
    getValue: (t) => t.iccid || '',
  },
  {
    key: 'installedAt',
    label: 'Installed',
    sortKey: 'installedAt',
    filterType: 'date-range',
    defaultVisible: false,
    getValue: (t) => t.installedAt || '',
    render: (t) => (t.installedAt ? formatDate(t.installedAt) : '—'),
  },
  {
    key: 'createdAt',
    label: 'Created',
    sortKey: 'createdAt',
    filterType: 'date-range',
    getValue: (t) => t.createdAt,
    render: (t) => formatDate(t.createdAt),
  },
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: (t, { onEdit, onReassign }) => (
      <div className="flex items-center gap-2">
        {t.mongoTransmitterId && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(t);
            }}
            className="text-atek-blue hover:underline text-sm cursor-pointer"
          >
            Edit
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onReassign(t);
          }}
          className="text-muted-foreground hover:text-foreground cursor-pointer"
          title="Reassign to another account"
        >
          <ArrowRightLeft className="h-4 w-4" />
        </button>
      </div>
    ),
    align: 'right',
  },
];

// =============================================================================
// localStorage Persistence
// =============================================================================

const VISIBILITY_STORAGE_KEY = 'atek-account-gateways-column-visibility';
const FILTERS_STORAGE_KEY_PREFIX = 'atek-account-gateways-column-filters';

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

function loadColumnFilters(accountId: string): Record<string, string> {
  try {
    const stored = localStorage.getItem(`${FILTERS_STORAGE_KEY_PREFIX}-${accountId}`);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return {};
}

// =============================================================================
// CSV Export Utilities
// =============================================================================

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function exportToCSV(items: TransmitterDetail[], columns: ColumnDef<TransmitterDetail>[], filename: string) {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function copyToClipboard(items: TransmitterDetail[], columns: ColumnDef<TransmitterDetail>[]): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}

// =============================================================================
// SortableHeader Component
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Filter Row Renderer
// =============================================================================

function renderColumnFilter(
  col: ColumnDef<TransmitterDetail>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
      catch { return []; }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
              {selected.length ? `${selected.length} selected` : '—'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// GatewaysTab Component (Mode B: server-side pagination + client-side refinement)
// =============================================================================

interface GatewaysTabProps {
  accountId: string;
}

const PAGE_SIZE = 50;

export default function GatewaysTab({ accountId }: GatewaysTabProps) {
  const queryClient = useQueryClient();

  // Server-side state
  const [page, setPage] = useState(0);

  // Client-side column filters (refinement within page)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => loadColumnFilters(accountId));
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);

  // Client-side sort (within page)
  const [sortKey, setSortKey] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const [isExportingAll, setIsExportingAll] = useState(false);

  // Modal state
  const [editingGatewayId, setEditingGatewayId] = useState<string | null>(null);
  const [reassignGateway, setReassignGateway] = useState<TransmitterDetail | null>(null);

  const dashboardData = queryClient.getQueryData<AccountDashboardData>(['account-dashboard', accountId]);
  const accountName = dashboardData?.account?.name || 'Current Account';

  // Persist column filters to localStorage
  useEffect(() => {
    localStorage.setItem(`${FILTERS_STORAGE_KEY_PREFIX}-${accountId}`, JSON.stringify(columnFilters));
  }, [columnFilters, accountId]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Extract text column filter values for server-side search
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([, value]) => {
        if (!value) return false;
        if (value.includes('>>')) return false;
        if (value.startsWith('[')) return false;
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  const [debouncedColumnSearch, setDebouncedColumnSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedColumnSearch(columnSearchText);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnSearchText]);

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;
  const visibleColumns = COLUMNS.filter((col) => isVisible(col.key));

  // Server-side data fetch
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['account-gateways', accountId, page, debouncedColumnSearch],
    queryFn: () =>
      getAccountGateways(accountId, {
        pageNumber: page,
        pageLength: PAGE_SIZE,
        search: debouncedColumnSearch || undefined,
      }),
  });

  const gateways = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

  // Client-side column filtering (AND-combined, within current page)
  const filteredItems = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return gateways;

    return gateways.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(item);

        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch { return true; }
        }

        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
      }),
    );
  }, [gateways, columnFilters]);

  // Client-side sorting (within filtered page)
  const sortedItems = useMemo(() => {
    if (!sortKey) return filteredItems;
    const col = COLUMNS.find((c) => c.sortKey === sortKey);
    if (!col) return filteredItems;

    return [...filteredItems].sort((a, b) => {
      const aVal = col.getValue(a);
      const bVal = col.getValue(b);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * sortDirection;
      }
      return String(aVal).localeCompare(String(bVal)) * sortDirection;
    });
  }, [filteredItems, sortKey, sortDirection]);

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
  };

  const handleExportAllGateways = async () => {
    try {
      setIsExportingAll(true);
      const allItems: TransmitterDetail[] = [];
      const batchSize = 100;
      let pageNum = 0;
      let totalCount = 0;

      do {
        const batch = await getAccountGateways(accountId, {
          pageNumber: pageNum,
          pageLength: batchSize,
          search: undefined,
        });
        allItems.push(...(batch.data || []));
        totalCount = batch.total ?? 0;
        pageNum++;
      } while (allItems.length < totalCount);

      exportToCSV(allItems, COLUMNS, 'account-gateways-all');
      toast.success(`Exported ${allItems.length} gateways`);
    } catch (e) {
      console.error('Export all gateways failed:', e);
      toast.error('Failed to export all gateways');
    } finally {
      setIsExportingAll(false);
    }
  };

  // Row helpers for column renderers
  const rowHelpers: GatewayRowHelpers = {
    onEdit: (t) => setEditingGatewayId(t.mongoTransmitterId),
    onReassign: setReassignGateway,
  };

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading gateways</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Gateways</CardTitle>
            <CardDescription>
              {filteredItems.length < gateways.length
                ? `Showing ${filteredItems.length} of ${gateways.length} on this page`
                : `Showing ${gateways.length} of ${total}`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                copyToClipboard(sortedItems, COLUMNS);
                toast.success('Copied to clipboard');
              }}
            >
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExportingAll}>
                  <Download className="h-4 w-4 mr-1" />
                  {isExportingAll ? 'Exporting...' : 'CSV'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export Gateways</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    exportToCSV(sortedItems, COLUMNS, 'account-gateways');
                    toast.success(`Exported ${sortedItems.length} rows`);
                  }}
                >
                  Export Page ({sortedItems.length} rows)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllGateways}>
                  Export All Gateways ({total})
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLUMNS.filter((c) => !c.noFilter).map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={isVisible(col.key)}
                    onCheckedChange={() => toggleColumn(col.key)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {/* ROW 1: Column filters (always visible) */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) => renderColumnFilter(col, columnFilters, setColumnFilter))}
              </TableRow>

              {/* ROW 2: Column labels + sort */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead key={col.key} className={cn(col.align === 'right' && 'text-right')}>
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    ) : (
                      col.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading state */}
              {isLoading && Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}

              {/* Empty state */}
              {!isLoading && sortedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                    No gateways found
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading && sortedItems.map((gateway) => (
                <TableRow
                  key={gateway.id}
                  className="hover:bg-muted/50"
                >
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        'text-sm',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.className,
                      )}
                    >
                      {col.render
                        ? col.render(gateway, rowHelpers)
                        : (String(col.getValue(gateway) ?? '') || '—')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Server-side pagination */}
        {totalPages > 1 && (
          <div className="border-t px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Gateway Edit Modal */}
      <TransmitterEditModal
        transmitterId={editingGatewayId}
        open={!!editingGatewayId}
        onOpenChange={(open) => {
          if (!open) setEditingGatewayId(null);
        }}
        organisationId={dashboardData?.account?.mongo_organisation_id}
        deviceType="gateways"
      />

      {/* Reassign Dialog */}
      <ReassignDialog
        entityType="transmitter"
        entityId={reassignGateway?.id || ''}
        entityLabel={reassignGateway?.name || reassignGateway?.devId || ''}
        currentAccountId={accountId}
        currentAccountName={accountName}
        open={!!reassignGateway}
        onOpenChange={(open) => { if (!open) setReassignGateway(null); }}
      />
    </Card>
  );
}
